import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ModalData } from '../../../model/modal-data.model';
import { SidebarData, SidebarType } from './stacked-sidebar.interface';
import { BaseService } from '../../../services/base.service';
import { BoardV3CardDetailComponent } from '../../board-v3/card-detail/card-detail.component';
import { BoardV3ContactDetailComponent } from '../../board-v3/contacts/contact-detail/contact-detail.component';
import { BoardV3CompanyDetailComponent } from '../../board-v3/companies/company-detail/company-detail.component';
import { TranslationManagerService, TranslationModule } from '../../../core/i18n';

/**
 * Позамаршрутні сайдбари відкриваються поверх будь-якого екрана, тому
 * маршрутне автозавантаження перекладів їх не покриває — оголошуємо
 * потрібні неймспейси тут (див. docs/I18N.md).
 */
const SIDEBAR_TRANSLATION_MODULES: Partial<Record<SidebarType, TranslationModule>> = {
  [SidebarType.Planner]: 'olx',
  [SidebarType.BuyOlxAdsBundles]: 'olx',
  [SidebarType.AdvertAction]: 'olx',
  [SidebarType.BuyOlxPackages]: 'olx',
  [SidebarType.BuyOlxPackageLegacy]: 'olx',
  [SidebarType.ChangeAdvert]: 'olx',
  [SidebarType.CreateAds]: 'olx',
  [SidebarType.IntegrationOlx]: 'olx',
  [SidebarType.AutomationLogDetails]: 'automation',
  [SidebarType.Chats]: 'channel',
  [SidebarType.CreateChannel]: 'channel',
  [SidebarType.ChannelTypes]: 'channel',
  [SidebarType.ChannelGroupForm]: 'channel',
  [SidebarType.NeuronAndAnotherCrm]: 'channel',
  [SidebarType.IntegrationAi]: 'channel',
  [SidebarType.Ticket]: 'support',
  [SidebarType.Site]: 'site',
  [SidebarType.CardV3]: 'stock',
  [SidebarType.WarehouseProductForm]: 'stock',
  [SidebarType.WarehouseProductImport]: 'stock',
  [SidebarType.WarehouseForm]: 'stock',
  [SidebarType.WarehouseDocument]: 'stock',
  [SidebarType.BoardV3WarehouseSettings]: 'stock',
  [SidebarType.BoardV3PaymentForm]: 'payments',
  [SidebarType.PaymentServiceForm]: 'payments',
  [SidebarType.NovaPoshtaSettings]: 'novaposhta',
  [SidebarType.VchasnoKasaSettings]: 'prro',
  [SidebarType.VchasnoKasaRegisterForm]: 'prro',
  [SidebarType.VchasnoKasaOperationDetail]: 'prro',
  [SidebarType.VchasnoKasaHistory]: 'prro',
  [SidebarType.VchasnoKasaReturn]: 'prro',
  [SidebarType.NovaPoshtaShipmentForm]: 'novaposhta',
  [SidebarType.NovaPoshtaLinkForm]: 'novaposhta',
  [SidebarType.TaskForm]: 'tasks',
  [SidebarType.TaskDetail]: 'tasks',
};

@Component({
  selector: 'app-stacked-sidebar',
  templateUrl: './stacked-sidebar.component.html',
  styleUrls: ['./stacked-sidebar.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('slideInOut', [
      state('enter', style({ transform: 'translateX(0)' })),
      state('leave', style({ transform: 'translateX(100%)' })),
      transition('enter => leave', [animate('200ms ease-in')]),
      transition('void => enter', [style({ transform: 'translateX(100%)' }), animate('200ms ease-out')]),
    ]),
  ],
})
export class StackedSidebarComponent implements OnInit {
  protected result?: any;
  protected type!: SidebarType;
  protected payload?: any;
  protected width!: string;
  protected maxWidth?: string;
  protected fullWidth: boolean = false;
  protected sidebarState: 'enter' | 'leave' = 'enter';
  @ViewChild('cardV3Detail') private cardV3Detail?: BoardV3CardDetailComponent;
  @ViewChild('contactDetail') private contactDetail?: BoardV3ContactDetailComponent;
  @ViewChild('companyDetail') private companyDetail?: BoardV3CompanyDetailComponent;
  /** A warehouse sidebar that asks before it closes */
  @ViewChild('closeGuard') private closeGuard?: { requestClose(): void };

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly translationManager = inject(TranslationManagerService);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalData<SidebarData>,
    private readonly dialogRef: MatDialogRef<StackedSidebarComponent>,
    protected readonly baseService: BaseService,
  ) {}

  ngOnInit(): void {
    this.width = this.data.width || '90vw';
    const maxWidth = this.data.maxWidth;
    this.maxWidth = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
    this.fullWidth = this.data.fullWidth ?? false;

    const data = this.data.data!;
    this.type = data.type;
    this.payload = data.payload;

    const translationModule = SIDEBAR_TRANSLATION_MODULES[this.type];
    if (translationModule) {
      this.translationManager
        .loadModuleTranslations(translationModule)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }

    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close());
  }

  protected get stackOffset(): number {
    const stacked = this.dialog.openDialogs.filter(
      (ref) =>
        (ref.componentInstance as { data?: ModalData<unknown> } | null)?.data?.component === StackedSidebarComponent,
    );
    const index = stacked.indexOf(this.dialogRef);
    return index === -1 ? 0 : stacked.length - 1 - index;
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: Event): void {
    const topDialog = this.dialog.openDialogs[this.dialog.openDialogs.length - 1];
    if (topDialog !== this.dialogRef) return;

    event.stopPropagation();
    this.close();
  }

  protected close(res?: any, bypassCloseGuard: boolean = false): void {
    if (!bypassCloseGuard && this.type === SidebarType.CardV3 && this.cardV3Detail) {
      this.cardV3Detail.requestClose();
      return;
    }
    if (!bypassCloseGuard && this.type === SidebarType.BoardV3ContactDetail && this.contactDetail) {
      this.contactDetail.requestClose();
      return;
    }
    if (!bypassCloseGuard && this.type === SidebarType.BoardV3CompanyDetail && this.companyDetail) {
      this.companyDetail.requestClose();
      return;
    }
    if (!bypassCloseGuard && this.closeGuard) {
      this.closeGuard.requestClose();
      return;
    }

    this.result = res;
    this.sidebarState = 'leave';
  }

  protected onAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'leave') {
      this.dialogRef.close(this.result);
    }
  }

  protected onBoardV3FieldsChanged(deletedFieldKey?: string | void): void {
    const current = (this.result ?? {}) as { changed?: boolean; deletedFieldKeys?: string[] };
    const deletedFieldKeys = [...(current.deletedFieldKeys ?? [])];

    if (deletedFieldKey) {
      deletedFieldKeys.push(deletedFieldKey);
    }

    this.result = {
      ...current,
      changed: true,
      deletedFieldKeys,
    };
  }

  protected closeWithCurrentResult(): void {
    this.close(this.result);
  }

  protected readonly SidebarType = SidebarType;
}
